// RebatePanel — REBATE v3 control surface (owner-approved design 2026-07-10).
//
// One page = the full money dial per category: platform fee IN (charged from
// users; CASINO only — cricket exempt) and turnover return OUT (house-funded
// pool X%, player share Y%, chain share X−Y split by P&L differences). Master
// kill switch on top; player/chain switches per card; cricket gets THREE
// independent cards (match odds / session / bookmaker) mapped by market type.
//
// BPG-6 (2026-08-05): board + small were physically merged into ONE "Casino"
// bucket — every non-cricket game now carries games.rebate_category='casino'.
// So this panel is down to a single Casino card + the three cricket cards, and
// the old game→category mapping grid is gone (there is only one bucket left).
//
// Backend: GET/PATCH /admin/commission/rebate-config. Nothing here touches
// money paths — the engine (task #57) reads this config.

import { useEffect, useMemo, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from '../../components/fieldStyles';

type Cat = { totalPct: number; playerPct: number; playerEnabled: boolean; chainEnabled: boolean };
type RebateCfg = { master: { enabled: boolean }; categories: Record<string, Cat> };
type Fees = { casino: number };

const r2 = (n: number) => Math.round(n * 100) / 100;
const inr = (n: number) => `₹${r2(n).toLocaleString('en-IN')}`;

const CRICKET: Array<{ key: string; title: string }> = [
  { key: 'cricket_match_odds', title: 'Match odds' },
  { key: 'cricket_session',    title: 'Session' },
  { key: 'cricket_bookmaker',  title: 'Bookmaker' },
];

function Switch({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200
                  ${on ? 'bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : 'bg-arena-300 dark:bg-arena-600'}
                  ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200 ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

const pctInput = `${INPUT_CLS} w-20 text-right !py-1.5 tabular-nums`;
const cardCls = 'rounded-2xl bg-white dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/40 p-4';

// One category card — the approved mockup's structure: X input → split bar →
// Y input + player switch → auto chain share + chain switch → live ₹ example
// (→ platform-fee row for the Casino category).
//
// W1 (owner bug report 2026-07-10): the PLATFORM FEE is a fully independent
// charge — it must stay active and editable even when the rebate is dead
// (X=0 / both switches off) or the master kill switch is OFF. Only the
// REBATE portion of the card dims/locks; the fee block never does.
function CatCard({ title, icon, cat, fee, onCat, onFee, killed }: {
  title: string; icon: string; cat: Cat;
  fee?: number | null;
  onCat: (p: Partial<Cat>) => void;
  onFee?: (v: number) => void;
  killed?: boolean;
}) {
  const chain = r2(Math.max(0, cat.totalPct - cat.playerPct));
  const dead = cat.totalPct === 0 || (!cat.playerEnabled && !cat.chainEnabled);
  const state = cat.totalPct === 0 ? '0% — dead'
    : !cat.playerEnabled && !cat.chainEnabled ? 'both off'
    : cat.playerEnabled && cat.chainEnabled ? 'player + chain'
    : cat.playerEnabled ? 'player only' : 'chain only';
  const parts: string[] = [];
  if (cat.playerEnabled && cat.playerPct > 0) parts.push(`player gets ${inr(cat.playerPct * 10)}`);
  if (cat.chainEnabled && chain > 0) parts.push(`chain shares ${inr(chain * 10)}`);

  return (
    <div className={cardCls}>
      <div className={`${dead || killed ? 'opacity-60' : ''} ${killed ? 'pointer-events-none' : ''}`}>
      <div className="flex items-center gap-2">
        <Icon name={icon} size={16} className="text-violet-600 dark:text-violet-300" />
        <span className="text-[13.5px] font-semibold text-neutral-900 dark:text-white">{title}</span>
        <span className="ml-auto text-[10.5px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60">{state}</span>
      </div>

      <div className="flex items-center justify-between gap-2 mt-3">
        <span className="text-[12px] text-arena-600 dark:text-neon-200/70">Total X% <span className="text-arena-400 dark:text-neon-200/60">(under 10)</span></span>
        <input type="number" step="0.1" min="0" max="9.99" className={pctInput} value={cat.totalPct}
               onChange={(e) => onCat({ totalPct: Math.min(9.99, Math.max(0, Number(e.target.value) || 0)) })} />
      </div>

      <div className="flex h-2 rounded-full overflow-hidden bg-arena-100 dark:bg-arena-900/60 mt-2.5">
        <div className="bg-emerald-500" style={{ width: cat.totalPct ? `${(cat.playerEnabled ? cat.playerPct : 0) / cat.totalPct * 100}%` : '0' }} />
        <div className="bg-violet-500"  style={{ width: cat.totalPct ? `${(cat.chainEnabled ? chain : 0) / cat.totalPct * 100}%` : '0' }} />
      </div>

      <div className="flex items-center justify-between gap-2 mt-2.5">
        <span className="text-[12px] text-arena-600 dark:text-neon-200/70">Player share Y%</span>
        <span className="flex items-center gap-2">
          <input type="number" step="0.1" min="0" className={pctInput} value={cat.playerPct}
                 onChange={(e) => onCat({ playerPct: Math.min(cat.totalPct, Math.max(0, Number(e.target.value) || 0)) })} />
          <Switch on={cat.playerEnabled} onChange={(v) => onCat({ playerEnabled: v })} />
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 mt-2.5">
        <span className="text-[12px] text-arena-600 dark:text-neon-200/70">Chain share (auto)</span>
        <span className="flex items-center gap-2">
          <span className="w-20 text-right text-[13.5px] font-bold tabular-nums text-neutral-900 dark:text-white">{chain}%</span>
          <Switch on={cat.chainEnabled} onChange={(v) => onCat({ chainEnabled: v })} />
        </span>
      </div>

      <p className="text-[11.5px] text-arena-500 dark:text-neon-200/60 mt-2.5 leading-snug">
        {cat.totalPct === 0
          ? 'Rebate 0% — the return is off. The platform fee below still charges.'
          : `On a ₹1,000 bet: ${parts.length ? parts.join(' · ') : 'nothing is paid'}${cat.playerEnabled && cat.chainEnabled ? '' : ' — the switched-off share stays with the house'}`}
      </p>
      </div>

      {fee != null && onFee && (
        <div className="mt-3 pt-3 border-t border-arena-100 dark:border-white/10">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[12px] text-amber-700 dark:text-amber-300">
              <Icon name="account_balance" size={14} />
              Platform fee <span className="text-arena-400 dark:text-neon-200/60">(charged from users)</span>
            </span>
            <span className={`text-[14px] font-bold tabular-nums ${fee > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-arena-400 dark:text-neon-200/60'}`}>
              {fee}%
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] text-arena-400 dark:text-neon-200/60 tabular-nums">0</span>
            <input
              type="range" min="0" max="25" step="0.5" value={fee}
              onChange={(e) => onFee(Number(e.target.value))}
              className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer
                         bg-arena-200 dark:bg-arena-600 accent-amber-500"
              aria-label="Platform fee percent"
            />
            <span className="text-[10px] text-arena-400 dark:text-neon-200/60 tabular-nums">25</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RebatePanel() {
  const [rebate, setRebate] = useState<RebateCfg | null>(null);
  const [fees, setFees] = useState<Fees | null>(null);
  const [saved, setSaved] = useState<string>('');   // JSON snapshot for dirty check
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setErr(null);
    try {
      const r = await adminApi.get<{ rebate: RebateCfg; fees: Fees }>('/admin/commission/rebate-config');
      setRebate(r.rebate); setFees(r.fees);
      setSaved(JSON.stringify({ rebate: r.rebate, fees: r.fees }));
    } catch (e) { setErr((e as ApiError).message || 'Failed to load'); }
  }
  useEffect(() => { load(); }, []);

  const dirty = useMemo(
    () => rebate && fees && JSON.stringify({ rebate, fees }) !== saved,
    [rebate, fees, saved]);

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  async function save() {
    if (!rebate || !fees) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.patch('/admin/commission/rebate-config', { rebate, fees });
      setSaved(JSON.stringify({ rebate, fees }));
      flash('Saved — applies from the next settlement');
    } catch (e) { setErr((e as ApiError).message || 'Save failed'); }
    finally { setBusy(false); }
  }

  const patchCat = (key: string) => (p: Partial<Cat>) =>
    setRebate((r) => {
      if (!r) return r;
      const cur = { ...r.categories[key], ...p };
      cur.playerPct = Math.min(cur.playerPct, cur.totalPct);
      return { ...r, categories: { ...r.categories, [key]: cur } };
    });

  if (err && !rebate) return <div className="max-w-4xl mx-auto p-4"><div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">{err}</div></div>;
  if (!rebate || !fees) return <div className="max-w-4xl mx-auto p-8 text-center text-arena-500 dark:text-neon-200/60 animate-pulse">Loading…</div>;

  const killed = !rebate.master.enabled;

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 space-y-4 pb-24">
      {/* Header + master kill switch */}
      <div className={cardCls}>
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                           bg-violet-50 text-violet-700 ring-1 ring-violet-100
                           dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
            <Icon name="cycle" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-[17px] font-bold text-neutral-900 dark:text-white leading-tight">Turnover return (rebate) + platform fees</h1>
            <p className="text-[12px] text-arena-600 dark:text-neon-200/70 mt-0.5">
              House pays the return on every human side's turnover, win or lose. The fee is charged from users. Cricket is exempt from fees.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60">Master</span>
            <Switch on={rebate.master.enabled} onChange={(v) => setRebate({ ...rebate, master: { enabled: v } })} />
          </div>
        </div>
        {killed && (
          <div className="mt-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-400/30 text-rose-800 dark:text-rose-200 px-3 py-2 text-[12.5px]">
            Master switch is OFF — no player and no broker receives any return anywhere, whatever the cards below say. Platform fees are independent: they stay active and editable.
          </div>
        )}
        <div className="flex items-center gap-4 mt-3 text-[11.5px] text-arena-600 dark:text-neon-200/70">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-emerald-500 inline-block" />Player share</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-violet-500 inline-block" />Chain share (split by P&amp;L differences, admin keeps its remainder)</span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Casino — one bucket for every non-cricket game (with platform fee; fee never locks) */}
        <div>
          <div className="flex items-center gap-2 px-1 pb-2">
            <Icon name="playing_cards" size={16} className="text-violet-600 dark:text-violet-300" />
            <span className="text-[12px] font-extrabold uppercase tracking-wider text-arena-700 dark:text-neon-200/85">Casino — every game except cricket</span>
            <span className="text-[11px] text-arena-500 dark:text-neon-200/60">one bucket for all board, small &amp; casino games</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <CatCard title="Casino" icon="playing_cards" killed={killed}
                     cat={rebate.categories.casino} onCat={patchCat('casino')}
                     fee={fees.casino} onFee={(v) => setFees({ ...fees, casino: v })} />
          </div>
        </div>

        {/* Cricket — three independent cards, no fee */}
        <div className={killed ? 'opacity-50 pointer-events-none' : ''}>
          <div className="flex items-center gap-2 px-1 pb-2">
            <Icon name="sports_cricket" size={16} className="text-emerald-600 dark:text-emerald-300" />
            <span className="text-[12px] font-extrabold uppercase tracking-wider text-arena-700 dark:text-neon-200/85">Cricket — three independent categories</span>
            <span className="text-[11px] text-arena-500 dark:text-neon-200/60">mapped by market type · no platform fee</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CRICKET.map((c) => (
              <CatCard key={c.key} title={c.title} icon="sports_cricket"
                       cat={rebate.categories[c.key]} onCat={patchCat(c.key)} />
            ))}
          </div>
        </div>
      </div>

      {/* Propagation note */}
      <div className="rounded-2xl bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-400/30 text-sky-800 dark:text-sky-200 px-4 py-3 text-[12.5px] flex items-start gap-2">
        <Icon name="podcasts" size={16} className="shrink-0 mt-0.5" />
        <span>Every change reflects on the broker ends (Hisaab gains a turnover-return income stream) and the player end (return credited with its own history line) — from the next settlement onward, never retroactive.</span>
      </div>

      {err && <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">{err}</div>}

      {/* Sticky save bar */}
      <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 transition-all ${dirty || toast ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <div className="flex items-center gap-3 rounded-2xl bg-white dark:bg-arena-800 border border-arena-200/60 dark:border-arena-600/60 shadow-xl px-4 py-2.5">
          {toast ? (
            <span className="text-[13px] font-semibold text-emerald-700 dark:text-emerald-300">{toast}</span>
          ) : (
            <>
              <span className="text-[13px] text-arena-600 dark:text-neon-200/70">Unsaved changes</span>
              <button type="button" onClick={save} disabled={busy}
                      className="rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-[13px] font-bold px-4 py-1.5 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
